import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("category") || "all";
  const initialSection = params.get("section") || "all";
  const queryFromUrl = params.get("q") || "";
  const [category, setCategory] = useState(initial);
  const [section, setSection] = useState(initialSection);
  const [searchTerm, setSearchTerm] = useState(queryFromUrl);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allSections, setAllSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("newest");

  const filterButtons = useMemo(
    () => [{ value: "all", label: "All" }, ...categories.map((c) => ({ value: c.slug, label: c.name }))],
    [categories],
  );

  // Sections that belong to the currently-selected category
  const sectionsForCategory = useMemo(() => {
    if (category === "all") return [];
    return allSections.filter((s) => s.category_slug === category);
  }, [allSections, category]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/sections").then(({ data }) => setAllSections(data)).catch(() => {});
  }, []);

  // Sync URL → local state when nav from header search
  useEffect(() => {
    setSearchTerm(params.get("q") || "");
    setCategory(params.get("category") || "all");
    setSection(params.get("section") || "all");
  }, [params]);

  useEffect(() => {
    setLoading(true);
    const apiParams = {};
    if (category !== "all") apiParams.category = category;
    if (section !== "all") apiParams.section = section;
    if (searchTerm.trim()) apiParams.q = searchTerm.trim();
    api.get("/products", { params: apiParams })
      .then(({ data }) => setProducts(data))
      .finally(() => setLoading(false));
  }, [category, section, searchTerm]);

  const sorted = useMemo(() => {
    const arr = [...products];
    if (sort === "price_asc") arr.sort((a, b) => a.price - b.price);
    if (sort === "price_desc") arr.sort((a, b) => b.price - a.price);
    return arr;
  }, [products, sort]);

  const writeParams = (cat, sec, q) => {
    const next = {};
    if (cat && cat !== "all") next.category = cat;
    if (sec && sec !== "all") next.section = sec;
    if (q && q.trim()) next.q = q.trim();
    setParams(next);
  };

  const handleCategory = (c) => {
    setCategory(c);
    setSection("all");  // Reset section when switching categories
    writeParams(c, "all", searchTerm);
  };

  const handleSection = (s) => {
    setSection(s);
    writeParams(category, s, searchTerm);
  };

  const clearSearch = () => {
    setSearchTerm("");
    writeParams(category, section, "");
  };

  return (
    <div data-testid="shop-page" className="max-w-7xl mx-auto px-6 py-12 sm:py-16">
      <div className="font-script text-3xl text-[#e8478e]">Browse The Goods</div>
      <h1 className="font-display text-5xl sm:text-7xl font-black text-[#1c1815] tracking-tight">Shop</h1>
      <p className="mt-3 text-[#4a4239] max-w-xl">Bold tees, instant digital downloads, and handmade crafts — pick your poison.</p>

      {searchTerm.trim() && (
        <div className="mt-6 inline-flex items-center gap-3 bg-[#2eb6a8]/10 border border-[#2eb6a8]/40 rounded-full pl-5 pr-2 py-2" data-testid="search-pill">
          <span className="text-sm text-[#1c1815]">
            Searching for <strong className="text-[#1f8d83]">"{searchTerm}"</strong>
          </span>
          <button onClick={clearSearch} data-testid="clear-search"
            className="bg-white hover:bg-[#efe5d4] text-[#1c1815] text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 rounded-full transition-colors">
            Clear
          </button>
        </div>
      )}

      <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          {filterButtons.map((c) => (
            <button
              key={c.value}
              data-testid={`filter-${c.value}`}
              onClick={() => handleCategory(c.value)}
              className={`text-[10px] font-bold uppercase tracking-[0.2em] px-5 py-2.5 rounded-full border transition-all ${
                category === c.value
                  ? "bg-[#2eb6a8] border-[#2eb6a8] text-white shadow-md"
                  : "bg-white border-[#e5dccd] text-[#4a4239] hover:border-[#e8478e] hover:text-[#e8478e]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <select
          data-testid="sort-select"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="bg-white border border-[#e5dccd] text-[#1c1815] text-sm rounded-full px-4 py-2.5 focus:border-[#2eb6a8] outline-none"
        >
          <option value="newest">Newest</option>
          <option value="price_asc">Price: Low → High</option>
          <option value="price_desc">Price: High → Low</option>
        </select>
      </div>

      {/* Section pills — only when a specific category is active and it has sections */}
      {sectionsForCategory.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="section-pills">
          <button
            type="button"
            onClick={() => handleSection("all")}
            data-testid="section-all"
            className={`text-[10px] font-bold uppercase tracking-[0.18em] px-3.5 py-1.5 rounded-full border transition-all ${
              section === "all"
                ? "bg-[#1c1815] border-[#1c1815] text-white"
                : "bg-white border-[#e5dccd] text-[#4a4239] hover:border-[#1c1815]"
            }`}
          >
            All
          </button>
          {sectionsForCategory.map((s) => {
            const active = section === s.slug;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => handleSection(s.slug)}
                data-testid={`section-${s.slug}`}
                style={active ? { backgroundColor: s.accent_color, borderColor: s.accent_color } : { borderColor: "#e5dccd" }}
                className={`text-[10px] font-bold uppercase tracking-[0.18em] px-3.5 py-1.5 rounded-full border transition-all ${
                  active ? "text-white" : "bg-white text-[#4a4239] hover:text-[#1c1815]"
                }`}
              >
                {s.name}
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {loading && Array(8).fill(0).map((_, i) => (
          <div key={i} className="aspect-square bg-[#efe5d4] rounded-2xl animate-pulse"/>
        ))}
        {!loading && sorted.map((p, i) => <ProductCard key={p.id} product={p} index={i}/>)}
      </div>

      {!loading && sorted.length === 0 && (
        <div className="text-center py-24">
          <div className="font-script text-3xl text-[#e8478e]">Nothing Here Yet</div>
          <p className="text-[#8a8076] mt-2">Try another category or check back soon.</p>
        </div>
      )}
    </div>
  );
}
